
interface ErrorDetails {
  message: string;
  code?: string;
  context?: string;
}

class SecureError extends Error {
  public readonly userMessage: string;
  public readonly code?: string;
  public readonly context?: string;

  constructor(userMessage: string, details?: ErrorDetails) {
    super(details?.message || userMessage);
    this.userMessage = userMessage;
    this.code = details?.code;
    this.context = details?.context;
  }
}

// Generic error messages for production
const GENERIC_ERRORS = {
  NETWORK: "Unable to connect. Please check your internet connection and try again.",
  AUTH: "Authentication failed. Please sign in again.",
  VALIDATION: "Please check your input and try again.",
  RATE_LIMIT: "Too many requests. Please wait a moment before trying again.",
  SERVER: "Something went wrong. Please try again later.",
  STORAGE: "Unable to save data. Please try again.",
  GENERATION: "Unable to generate content. Please try again.",
} as const;

export const handleSecureError = (error: any, context: string = ''): SecureError => {
  // Log detailed error for debugging (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.error(`[${context}] Error details:`, {
      message: error?.message,
      stack: error?.stack,
      code: error?.code,
      details: error?.details
    });
  }

  // Determine user-friendly message based on error type
  let userMessage: string = GENERIC_ERRORS.SERVER;
  let errorCode = error?.code;

  if (error?.message?.includes('network') || error?.code === 'NETWORK_ERROR') {
    userMessage = GENERIC_ERRORS.NETWORK;
  } else if (error?.message?.includes('auth') || error?.code === 'PGRST301') {
    userMessage = GENERIC_ERRORS.AUTH;
  } else if (error?.message?.includes('validation') || error?.code?.includes('23505')) {
    userMessage = GENERIC_ERRORS.VALIDATION;
  } else if (error?.message?.includes('rate limit')) {
    userMessage = GENERIC_ERRORS.RATE_LIMIT;
  } else if (error?.message?.includes('storage')) {
    userMessage = GENERIC_ERRORS.STORAGE;
  } else if (context.includes('generation')) {
    userMessage = GENERIC_ERRORS.GENERATION;
  }

  return new SecureError(userMessage, {
    message: error?.message || 'Unknown error',
    code: errorCode,
    context
  });
};

// Secure logging function that doesn't expose sensitive data
export const secureLog = (level: 'info' | 'warn' | 'error', message: string, data?: any) => {
  if (process.env.NODE_ENV === 'development') {
    const sanitizedData = data ? sanitizeLogData(data) : undefined;
    console[level](`[${new Date().toISOString()}] ${message}`, sanitizedData);
  }
};

const sanitizeLogData = (data: any): any => {
  if (typeof data === 'string') {
    // Remove potential sensitive patterns
    return data.replace(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi, '[EMAIL]')
               .replace(/(Bearer\s+[A-Za-z0-9-._~+/]+=*)/gi, '[TOKEN]')
               .replace(/(\b\d{4}\s?\d{4}\s?\d{4}\s?\d{4}\b)/g, '[CARD]');
  }
  
  if (typeof data === 'object' && data !== null) {
    const sanitized: any = {};
    for (const [key, value] of Object.entries(data)) {
      if (['password', 'token', 'key', 'secret', 'auth'].some(sensitive => 
          key.toLowerCase().includes(sensitive))) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = sanitizeLogData(value);
      }
    }
    return sanitized;
  }
  
  return data;
};
